import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Expense,
  ExpensesListResponse,
  ExpenseType,
} from '@stfors/shared-types';
import { api } from '../client';

export interface ExpenseListFilters {
  /** `'none'` = general-business only; positive = project; omit = all. */
  projectId?: number | 'none';
  /** Expenses linked to this asset (migration 0043). */
  assetId?: number;
}

// `'none'` filters to general-business expenses (project_id IS NULL); a
// positive number filters to that project; undefined returns all.
export function useExpenses(projectIdOrFilters?: number | 'none' | ExpenseListFilters) {
  const filters: ExpenseListFilters =
    typeof projectIdOrFilters === 'object' && projectIdOrFilters !== null
      ? projectIdOrFilters
      : { projectId: projectIdOrFilters };
  const params = new URLSearchParams();
  if (filters.projectId !== undefined) params.set('project_id', String(filters.projectId));
  if (filters.assetId !== undefined) params.set('asset_id', String(filters.assetId));
  const qs = params.toString();
  return useQuery({
    queryKey: ['expenses', {
      project_id: filters.projectId ?? null,
      asset_id: filters.assetId ?? null,
    }],
    queryFn: ({ signal }) =>
      api.get<ExpensesListResponse>(
        qs.length > 0 ? `/api/expenses?${qs}` : '/api/expenses',
        signal,
      ),
  });
}

export interface CreateExpenseInput {
  /** NULL for general business expenses (migration 0023). */
  project_id: number | null;
  type: ExpenseType;
  /** Total paid — NUMERIC(12,2) string. Equals subtotal + tax_amount. */
  amount: string;
  /** Sales tax portion of `amount`. Defaults server-side to "0". */
  tax_amount?: string;
  date: number;
  /** Soft FK into vendors (migration 0024). The API snapshots the
   *  vendor's display name into the row, so callers don't need to also
   *  send `vendor`. NULL means "no vendor selected". */
  vendor_id?: number | null;
  /** Free-text vendor — used only when vendor_id is NULL (legacy / one-off). */
  vendor?: string;
  receipt_number?: string;
  tax_category?: string;
  reimbursable?: boolean;
  notes?: string;
  mileage?: string;
  receipt_path?: string;
  asset_id?: number | null;
  /** The project's matter this cost belongs to (0061). */
  matter_id?: number | null;
}

/** One expense — the source of "Duplicate and edit" on New Expense. */
export function useExpense(id: number | null) {
  return useQuery({
    queryKey: ['expenses', 'one', id],
    queryFn: ({ signal }) => api.get<{ expense: Expense }>(`/api/expenses/${id}`, signal),
    enabled: id !== null,
  });
}

export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateExpenseInput) =>
      api.post<{ expense: Expense }>('/api/expenses', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['expenses'] });
      void qc.invalidateQueries({ queryKey: ['project-matters'] });
      void qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export interface UpdateExpenseInput {
  id: number;
  type?: string;
  amount?: string;
  tax_amount?: string;
  date?: number;
  project_id?: number | null;
  vendor_id?: number | null;
  vendor?: string | null;
  receipt_number?: string | null;
  tax_category?: string | null;
  notes?: string | null;
  mileage?: string | null;
  reimbursable?: boolean;
  receipt_path?: string | null;
  asset_id?: number | null;
  matter_id?: number | null;
}

export function useUpdateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateExpenseInput) =>
      api.patch<{ expense: Expense }>(`/api/expenses/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['expenses'] });
      void qc.invalidateQueries({ queryKey: ['project-matters'] });
      void qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useDeleteExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/expenses/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['expenses'] });
      void qc.invalidateQueries({ queryKey: ['project-matters'] });
    },
  });
}

export function useApproveExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, approve }: { id: number; approve: boolean }) =>
      api.post<{ expense: Expense }>(
        `/api/expenses/${id}/${approve ? 'approve' : 'unapprove'}`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['expenses'] });
      void qc.invalidateQueries({ queryKey: ['project-matters'] });
    },
  });
}

export function useReimburseExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reimburse }: { id: number; reimburse: boolean }) =>
      api.post<{ expense: Expense }>(
        `/api/expenses/${id}/${reimburse ? 'reimburse' : 'unreimburse'}`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['expenses'] });
      void qc.invalidateQueries({ queryKey: ['project-matters'] });
    },
  });
}
